import React, { useCallback } from 'react';
import type { ScaleProp, VoidCallback } from '../common-types';
import type { ValidIconName } from './Icon';
import type { DefaultTooltipOptions } from '../../';
import {
  noPropagationClickHandler,
  useLayerDeferedVisibility
} from '../../lib';
import { ButtonIcon } from './internal';
import { useHover } from 'react-laag';
import { DEFAULT_HOVER_CONFIG, DEFAULT_PLACEMENT_CONFIG } from '../../util';

/**
 * {@link ScaleProp} extended by 'default' option
 */
type ExtendedScaleProp =
  | {
      scale?: 'default';
    }
  | ScaleProp;

/**
 * Props for a {@link Button} component.
 */
export type ButtonProps = Omit<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >,
  'onClick' | 'button' | 'children'
> &
  DefaultTooltipOptions &
  ExtendedScaleProp & {
    /**
     * The button variant that is used.
     * - 'primary' = orange button
     * - 'gray' = regular gray button
     * - 'tertiary' = button with background of the same color as its parent but gray outline
     */
    variant: 'primary' | 'gray' | 'tertiary' | 'ios';

    /**
     * The click handler of the button.
     */
    onClick?: VoidCallback;

    /**
     * The type of the button.
     *
     * `submit` buttons are pressed by the browser when pressing enter in any input inside a surrounding form.
     */
    type?: 'button' | 'submit' | 'reset';

    text: string;

    icon?: ValidIconName;

    showIconAndText?: boolean;
  };

/**
 * A simple Button component.
 *
 * @param props a {@link ButtonProps} object.
 * These are simply the properties of a regular <code><button></code> tag
 * with an additional mandatory 'variant' flag to determine what kind of button variant is used.
 * @return the created {@link React.JSX.Element}
 * @constructor
 */
export function Button({
  variant,
  // className property that was passed
  className,
  onClick,
  type = 'button',
  text,
  icon,
  tooltipOptions,
  scale = 'default',
  showIconAndText = false,
  // the remaining properties that will be passed through to the primitive button
  ...buttonProps
}: Readonly<ButtonProps>): React.JSX.Element {
  // add button css class depending on variant & scale
  const cName = `ehs-button button-${variant} button-${scale}
  ${className ?? ''} 
  ${(icon && 'icon-button') || ''}
  ${(showIconAndText && 'show-icon-and-text') || ''}`;

  const [isOpen, hoverProps] = useHover(DEFAULT_HOVER_CONFIG);
  const { layerProps, triggerProps, renderLayer } = useLayerDeferedVisibility({
    isOpen,
    ...DEFAULT_PLACEMENT_CONFIG,
    ...tooltipOptions
  });

  // create click handler which doesn't propagate events and which hides the tooltip automatically
  const onClickHidingTooltip = useCallback(() => {
    hoverProps.onMouseLeave();
    onClick?.();
  }, [onClick, hoverProps]);

  const onClickHandler = noPropagationClickHandler(onClickHidingTooltip);

  // return rendered button
  if (icon && showIconAndText == false) {
    return (
      <>
        {isOpen &&
          renderLayer(
            <div {...layerProps} className="ehs-tooltip">
              {text}
            </div>
          )}
        <button
          {...buttonProps}
          {...triggerProps}
          {...hoverProps}
          type={type}
          onClick={onClickHandler}
          className={cName.trim()}
          aria-label={text}
        >
          <ButtonIcon iconName={icon} />
        </button>
      </>
    );
  } else {
    return (
      <button
        {...buttonProps}
        type={type}
        onClick={onClickHandler}
        className={cName.trim()}
      >
        {icon && showIconAndText && <ButtonIcon iconName={icon} />}
        {text}
      </button>
    );
  }
}
